import React, {useCallback, useEffect, useLayoutEffect} from 'react';
import {useLocation} from '@docusaurus/router';
import {useColorMode, useThemeConfig} from '@docusaurus/theme-common';
import ColorModeToggle from '@theme/ColorModeToggle';
import styles from './styles.module.css';

const DOCS_COLOR_MODE_KEY = 'pdfbolt-docs-color-mode';
const DOCUSAURUS_COLOR_MODE_KEY = 'theme';
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function isDocsOrBlogPath(pathname) {
  return /^\/(docs|blog)(\/|$)/.test(pathname);
}

function getStoredDocsColorMode() {
  try {
    return window.localStorage.getItem(DOCS_COLOR_MODE_KEY);
  } catch (error) {
    return null;
  }
}

function storeColorMode(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // Ignore storage errors and keep the in-memory Docusaurus state.
  }
}

export default function NavbarColorModeToggle({className}) {
  const {pathname} = useLocation();
  const navbarStyle = useThemeConfig().navbar.style;
  const {colorMode, setColorMode} = useColorMode();
  const scopedPage = isDocsOrBlogPath(pathname);

  useIsomorphicLayoutEffect(() => {
    const root = document.documentElement;

    if (!scopedPage) {
      root.setAttribute('data-theme', 'light');
      root.removeAttribute('data-pdfbolt-docs-color-scope');
      storeColorMode(DOCUSAURUS_COLOR_MODE_KEY, 'light');

      if (colorMode !== 'light') {
        setColorMode('light', {persist: false});
      }

      return;
    }

    const storedColorMode = getStoredDocsColorMode();
    const nextColorMode = storedColorMode === 'dark' ? 'dark' : 'light';
    root.setAttribute('data-theme', nextColorMode);
    root.setAttribute('data-pdfbolt-docs-color-scope', 'true');

    if (colorMode !== nextColorMode) {
      setColorMode(nextColorMode, {persist: false});
    }
  }, [colorMode, pathname, scopedPage, setColorMode]);

  const handleChange = useCallback(
    (nextColorMode) => {
      storeColorMode(DOCS_COLOR_MODE_KEY, nextColorMode);
      document.documentElement.setAttribute(
        'data-pdfbolt-docs-color-scope',
        'true',
      );
      setColorMode(nextColorMode, {persist: false});
    },
    [setColorMode],
  );

  return (
    <div
      className={[className, styles.colorModeToggleSlot]
        .filter(Boolean)
        .join(' ')}
      aria-hidden={!scopedPage}
    >
      {scopedPage && (
        <ColorModeToggle
          buttonClassName={
            navbarStyle === 'dark'
              ? styles.darkNavbarColorModeToggle
              : undefined
          }
          value={colorMode}
          onChange={handleChange}
        />
      )}
    </div>
  );
}
